/**
 * Angebot anlegen.
 *
 * Drei Schritte – Kategorie, Kunde, Zuordnung – und rechts die Live-Vorschau des
 * Datensatznamens. Beim Speichern entstehen zusätzlich der SharePoint-Ordner und
 * das Monday-Item unter demselben Namen.
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { useKora, useMeta } from '../zustand';
import { KundenModal } from '../modals/KundenModal';
import { Kundenvorschlaege, useKundensuche } from '../komponenten/Kundensuche';
import { nameZerlegen } from '../hilfen/format';
import type { Angebot, Kunde } from '../typen';
import './neu.css';

interface Formular {
  kategorie: string | null;
  kundeId: number | null;
  name: string;
  kalkulant: string;
  /** Leer heißt: wie der Kalkulant. */
  versender: string;
}

export function NeuesAngebot() {
  const navigate = useNavigate();
  const meta = useMeta();
  const { nutzer, toastZeigen } = useKora();
  const queryClient = useQueryClient();

  const [f, setF] = useState<Formular>({
    kategorie: null,
    kundeId: null,
    name: '',
    kalkulant: nutzer?.name ?? meta.team[0]?.name ?? '',
    versender: '',
  });
  /**
   * Kundenmaske. `null` = zu, `{ kundeId: null }` = neuer Kunde,
   * `{ kundeId: n }` = bestehenden Kunden bearbeiten.
   */
  const [kundenModal, setKundenModal] = useState<{ kundeId: number | null } | null>(null);
  const [fehler, setFehler] = useState('');

  const nummerAbfrage = useQuery({
    queryKey: ['naechste-angebotsnummer'],
    queryFn: () => api.hole<{ nr: number }>('/angebote/naechste-nummer'),
  });

  const kundenAbfrage = useQuery({
    queryKey: ['kunden'],
    queryFn: () => api.hole<Kunde[]>('/kunden'),
  });

  const angeboteAbfrage = useQuery({
    queryKey: ['angebote'],
    queryFn: () => api.hole<Angebot[]>('/angebote'),
  });

  // Live-Suche in der Kundendatenbank – dieselbe Mechanik wie in der Kundenmaske.
  const suche = useKundensuche(f.name, Boolean(f.kundeId));

  /*
   * Die Dublettenprüfung sitzt jetzt in der Kundenmaske, wo der Kunde entsteht.
   * Hier wäre sie eine zweite Stelle für dieselbe Frage - und da der Kunde
   * ohnehin aus dem Stamm kommt, gibt es hier nichts mehr zu prüfen.
   */

  const nr = nummerAbfrage.data?.nr ?? 0;

  /** Der verknüpfte Kunde – Quelle für Anschrift, Ort und Vorschau. */
  const bestand = f.kundeId ? kundenAbfrage.data?.find((k) => k.id === f.kundeId) : null;

  /** Nachname bzw. prägnanter Namensteil – gleiche Regel wie am Server. */
  const nachname = useMemo(() => {
    const roh = f.name.replace(/^(Ing\.|Dr\.|DI|Mag\.|Familie|Zimmerei|Bauträger)\s+/i, '').trim();
    const teile = roh.split(/\s+/).filter(Boolean);
    const letzte = teile[teile.length - 1] ?? '';
    if (/^(GmbH|e\.U\.|KG|OG|AG)$/i.test(letzte) && teile.length > 1) {
      return teile[teile.length - 2] ?? letzte;
    }
    return letzte;
  }, [f.name]);

  /*
   * Muss zeichengenau dem entsprechen, was `ordnerNameFuer` am Server bildet –
   * sonst verspricht die Vorschau einen anderen Ordnernamen, als entsteht.
   * Fehlt die PLZ, steht wie dort nur der Ort in der Klammer.
   */
  const gegend = bestand?.ortZeile?.trim() || 'PLZ Ort';
  const vorschau = `${nr || '…'}_${f.kategorie ?? '??'}_${nachname || 'Nachname'} (${gegend})`;

  /**
   * Was fehlt noch, bevor der Vorgang entstehen darf?
   *
   * Ist ein Bestandskunde verknüpft, zählen nur Kategorie und Kalkulant – die
   * Kontaktdaten stehen bereits im Kundenstamm und werden hier nur angezeigt.
   * Entsteht dagegen ein neuer Kunde, gelten dieselben Pflichtfelder wie in der
   * Kundenmaske: Wer später anrufen oder anschreiben soll, braucht Anschrift,
   * Mail und Telefon. Die PLZ steht seit der Umstellung auch im Ordnernamen.
   */
  /**
   * Was fehlt noch, bevor der Vorgang entstehen darf?
   *
   * Der Kunde muss aus dem Kundenstamm kommen – entweder gefunden oder über
   * die volle Maske angelegt. Adresse und Erreichbarkeit hier noch einmal
   * abzufragen wäre eine zweite Eingabestelle für dieselben Daten, mit dem
   * üblichen Ergebnis: Sie laufen auseinander.
   */
  const fehlend = useMemo(() => {
    const luecken: string[] = [];
    if (!f.kategorie) luecken.push('Kategorie');
    if (!f.kundeId) luecken.push('Kunde aus dem Kundenstamm');
    if (!f.kalkulant.trim()) luecken.push('Kalkulant');
    return luecken;
  }, [f]);

  const gueltig = fehlend.length === 0;

  /** Roter Stern – bei verknüpftem Bestandskunden entfällt er für dessen Felder. */
  const Pflicht = ({ nurNeu = false }: { nurNeu?: boolean }) =>
    nurNeu && f.kundeId ? null : <span className="neu-pflicht">*</span>;

  /**
   * Adresse und Kontakt gehören dem Kunden, nicht dem Angebot.
   *
   * Ist ein Bestandskunde verknüpft, ignoriert der Server diese Angaben – er
   * nimmt den gespeicherten Datensatz. Eingabefelder wären hier also eine
   * Einladung zu Eingaben, die spurlos verschwinden; auch gesperrte Felder
   * bleiben Felder. Stattdessen steht der Kunde da, wie man einen Adressaten
   * schreibt – Name, Straße, PLZ Ort, abgesetzt davon die Erreichbarkeit.
   * So sieht der Kalkulant auf einen Blick, ob er den richtigen erwischt hat.
   */
  const ausKundenstamm = Boolean(f.kundeId);

  const bestandAngebote = bestand
    ? (angeboteAbfrage.data ?? [])
        .filter((a) => a.kunde.id === bestand.id)
        .map((a) => a.prefix)
        .join(' + ')
    : '';

  const anlegen = useMutation({
    mutationFn: () =>
      api.sende<{ angebot: Angebot; sync: { ok: boolean; fehler: string | null } }>('/angebote', {
        kategorie: f.kategorie,
        kundeId: f.kundeId,
        name: f.name.trim(),
        kalkulant: f.kalkulant,
        versender: f.versender || f.kalkulant,
      }),
    onSuccess: (antwort) => {
      void queryClient.invalidateQueries();
      if (antwort.sync.ok) {
        toastZeigen('Angebot angelegt – Monday-Item und SharePoint-Ordner sind erzeugt.');
      } else {
        toastZeigen('Angebot angelegt – der Abgleich meldet ein Problem, siehe Hinweis am Angebot.');
      }
      navigate(`/angebot/${antwort.angebot.id}`);
    },
    onError: (fe) => setFehler(fe instanceof Error ? fe.message : 'Anlegen fehlgeschlagen.'),
  });

  function setzen<K extends keyof Formular>(feld: K, wert: Formular[K]) {
    setF((alt) => ({ ...alt, [feld]: wert }));
    setFehler('');
  }

  function kundeUebernehmen(k: Kunde) {
    setF((alt) => ({
      ...alt,
      kundeId: k.id,
      name: k.name,
    }));
    suche.schliessen();
  }

  return (
    <div className="neu">
      <div className="neu-spalte">
        <div>
          <button type="button" className="neu-zurueck" onClick={() => navigate('/cockpit')}>
            ← Vertriebscockpit
          </button>
          <div className="eh-kicker" style={{ marginTop: 14 }}>
            Neuanlage
          </div>
          <div className="eh-titel">Angebot anlegen</div>
        </div>

        {/* --- Schritt 1: Kategorie --- */}
        <div className="neu-karte">
          <div className="neu-schritt">
            <span className="neu-nummer">1</span>
            <span className="neu-schritt-titel">Kategorie</span>
            <span className="neu-schritt-hinweis">bestimmt Präfix und laufende Nummer</span>
          </div>
          <div className="neu-kategorien">
            {meta.kategorien.map((c) => (
              <button
                key={c.prefix}
                type="button"
                className={`neu-kategorie ${f.kategorie === c.prefix ? 'aktiv' : ''}`}
                onClick={() => setzen('kategorie', c.prefix)}
              >
                <span className="neu-kategorie-prefix">{c.prefix}</span>
                <span className="neu-kategorie-label">{c.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* --- Schritt 2: Kunde --- */}
        <div className="neu-karte">
          <div className="neu-schritt">
            <span className="neu-nummer">2</span>
            <span className="neu-schritt-titel">Kunde</span>
            <span className="neu-schritt-hinweis">
              Duplikat-Check über Mail, Telefon und Adresse
            </span>
            <button
              type="button"
              className="neu-kundenmaske"
              onClick={() => setKundenModal({ kundeId: null })}
            >
              Kunden neu anlegen +
            </button>
          </div>

          {bestand && (
            <div className="neu-bestand">
              <div>
                <strong>Bestandskunde verknüpft:</strong> {bestand.name}
                {bestandAngebote ? ` · ${bestandAngebote} bereits im System` : ''}
              </div>
              <div className="neu-bestand-werkzeuge">
                {/*
                  Tippfehler fallen oft erst auf, wenn der Name in der Vorschau
                  steht. Dann soll man ihn hier richtigstellen können, ohne den
                  Vorgang zu verlassen und im Kundenstamm zu suchen.
                */}
                <button
                  type="button"
                  className="neu-stift"
                  title="Kontaktdaten dieses Kunden bearbeiten"
                  onClick={() => setKundenModal({ kundeId: bestand.id })}
                >
                  ✎
                </button>
                <button
                  type="button"
                  className="neu-loesen"
                  onClick={() => {
                    // Nur lösen, nicht gleich wieder suchen: Der Name steht ja
                    // noch im Feld, die Liste würde denselben Kunden anbieten.
                    setzen('kundeId', null);
                    suche.schliessen();
                  }}
                >
                  Lösen
                </button>
              </div>
            </div>
          )}

          <div className="neu-raster">
            <label className="neu-feld neu-voll ks-feld">
              <span className="neu-feldlabel">
                <span>
                  Kundenname <Pflicht />
                </span>
                <span className="neu-suchstatus">
                  {f.kundeId
                    ? 'Bestandskunde verknüpft'
                    : f.name.trim().length >= 2
                      ? 'live gesucht'
                      : 'ab 2 Zeichen wird gesucht'}
                </span>
              </span>
              {/*
                Bedienbar ohne Maus: Pfeiltasten wählen, Enter übernimmt,
                Escape blendet aus. Wer viele Kunden anlegt, tippt und drückt
                Enter – die Hand bleibt auf der Tastatur.
              */}
              <input
                className="neu-eingabe"
                value={f.name}
                placeholder="Tippen – sucht live in der Kundendatenbank"
                role="combobox"
                aria-expanded={suche.offen}
                aria-autocomplete="list"
                onChange={(e) => {
                  setF((alt) => ({ ...alt, name: e.target.value, kundeId: null }));
                  suche.oeffnen();
                  setFehler('');
                }}
                onKeyDown={(e) => suche.taste(e, kundeUebernehmen, () => setKundenModal({ kundeId: null }))}
              />

              <Kundenvorschlaege
                suche={f.name}
                suchstand={suche}
                onWaehlen={kundeUebernehmen}
                zusatz={(k) => {
                  const anzahl = (angeboteAbfrage.data ?? []).filter(
                    (a) => a.kunde.id === k.id,
                  ).length;
                  return (
                    <span className="ks-anzahl">
                      {anzahl === 0
                        ? 'noch kein Angebot'
                        : anzahl === 1
                          ? '1 Angebot'
                          : `${anzahl} Angebote`}
                    </span>
                  );
                }}
                neuAktion={{
                  label: 'Neuen Kunden anlegen',
                  hinweis: 'Volle Kundenmaske mit Adresse, Kontakt und Merkmalen',
                  onAuswahl: () => setKundenModal({ kundeId: null }),
                }}
              />
            </label>

            {ausKundenstamm && bestand ? (
              /* Wie ein Adressat auf einem Brief - Name, Straße, PLZ Ort. */
              <div className="neu-adressat neu-voll">
                <div className="neu-adressat-kopf">
                  <span>Gewählter Kunde</span>
                  <span className="neu-adressat-nr">Nr. {bestand.kundenNr}</span>
                </div>
                <address className="neu-adressat-block">
                  <span className="neu-adressat-name">{bestand.name}</span>
                  <span>{bestand.strasse || '— keine Straße hinterlegt'}</span>
                  <span>{bestand.ortZeile || '— kein Ort hinterlegt'}</span>
                </address>
                <div className="neu-adressat-kontakt">
                  <span>
                    {[bestand.mobil, bestand.mobilGeschaeftlich].filter(Boolean).join(' · ') ||
                      '— keine Mobilnummer hinterlegt'}
                  </span>
                  <span>{bestand.mail || '— keine E-Mail hinterlegt'}</span>
                </div>
              </div>
            ) : (
              <div className="neu-kundenhinweis neu-voll">
                Oben tippen und einen Kunden wählen – oder über <strong>Neuen Kunden anlegen</strong>
                {' '}einen neuen erfassen. Anschrift und Erreichbarkeit stehen im Kundenstamm und
                erscheinen hier, sobald der Kunde verknüpft ist.
              </div>
            )}
          </div>

        </div>

        {/* --- Schritt 3: Zuordnung --- */}
        <div className="neu-karte">
          <div className="neu-schritt">
            <span className="neu-nummer">3</span>
            <span className="neu-schritt-titel">Zuordnung</span>
          </div>
          <div className="neu-raster neu-raster--zwei">
            <label className="neu-feld">
              <span className="neu-feldlabel">
                Kalkulant / Verantwortlich <Pflicht />
              </span>
              <select
                className="neu-eingabe"
                value={f.kalkulant}
                onChange={(e) => setzen('kalkulant', e.target.value)}
              >
                {meta.team.map((t) => (
                  <option key={t.name} value={t.name}>
                    {t.name}
                  </option>
                ))}
              </select>
            </label>
            {/*
              Versender getrennt vom Kalkulanten.
              Im Regelfall dieselbe Person – deshalb folgt das Feld dem
              Kalkulanten, solange niemand es anfasst. Es kann aber
              auseinanderfallen, und dann zählt der Versender: Aus seinem
              Postfach geht die Erinnerung, mit seiner Signatur, und an ihn
              antwortet der Kunde.
            */}
            <label className="neu-feld">
              <span className="neu-feldlabel">Versender</span>
              <select
                className="neu-eingabe"
                value={f.versender || f.kalkulant}
                onChange={(e) => setzen('versender', e.target.value)}
              >
                {meta.team.map((t) => (
                  <option key={t.name} value={t.name}>
                    {t.name}
                  </option>
                ))}
              </select>
              <span className="neu-hinweis">
                Versendet die Erinnerung aus seinem Postfach. Standardmäßig der Kalkulant.
              </span>
            </label>

            <label className="neu-feld">
              <span className="neu-feldlabel">Startstatus</span>
              <input className="neu-eingabe neu-gesperrt" value="START" readOnly />
            </label>
          </div>
        </div>
      </div>

      {/* --- Vorschau --- */}
      <div className="neu-seitenspalte">
        <div className="neu-vorschau">
          <div className="neu-vorschau-label">Vorschau Datensatzname</div>
          <div className="neu-vorschau-name">{vorschau}</div>
          <div className="neu-vorschau-raster">
            <div>
              <div className="neu-vorschau-klein">Laufende Nummer</div>
              <div className="neu-vorschau-wert">{nr || '…'}</div>
            </div>
            <div>
              <div className="neu-vorschau-klein">Präfix</div>
              <div className="neu-vorschau-wert">
                {f.kategorie
                  ? `${f.kategorie} · ${meta.kategorien.find((k) => k.prefix === f.kategorie)?.label ?? ''}`
                  : '— Kategorie wählen'}
              </div>
            </div>
            <div>
              <div className="neu-vorschau-klein">Monday-Item</div>
              <div className="neu-vorschau-text">
                {meta.integrationen.monday ? 'wird angelegt' : 'nicht konfiguriert'}
              </div>
            </div>
            <div>
              <div className="neu-vorschau-klein">SharePoint</div>
              <div className="neu-vorschau-text">
                {meta.integrationen.graph ? 'Ordner wird erzeugt' : 'nicht konfiguriert'}
              </div>
            </div>
          </div>
        </div>

        {/*
          Der Knopf bleibt bedienbar, auch wenn etwas fehlt. Ein gesperrter
          Knopf ohne Begründung ist die häufigste Sackgasse in Formularen: Man
          sieht, dass es nicht geht, aber nicht warum. Hier sagt der Klick es.
        */}
        <button
          type="button"
          className={`neu-speichern ${gueltig ? '' : 'unvollstaendig'}`}
          disabled={anlegen.isPending}
          onClick={() => {
            if (!gueltig) {
              setFehler(
                `Bitte noch ausfüllen: ${fehlend.join(', ')}. ` +
                  'Die mit * gekennzeichneten Felder sind Pflicht.',
              );
              return;
            }
            setFehler('');
            anlegen.mutate();
          }}
        >
          {anlegen.isPending ? 'Wird angelegt …' : 'Angebot anlegen'}
        </button>

        <div className={`neu-speichern-hinweis ${fehler ? 'warnend' : ''}`}>
          {fehler ||
            (gueltig
              ? 'Erzeugt den Datensatz, das Monday-Item und den SharePoint-Ordner unter identischem Namen.'
              : `Noch offen: ${fehlend.join(', ')}`)}
        </div>
      </div>

      {kundenModal && (
        <KundenModal
          kundeId={kundenModal.kundeId}
          vorbelegung={kundenModal.kundeId === null ? nameZerlegen(f.name) : undefined}
          onSchliessen={() => setKundenModal(null)}
          onGespeichert={(k) => {
            // Auch beim Bearbeiten: Die geänderten Daten sollen sofort im
            // Formular und in der Vorschau stehen.
            kundeUebernehmen(k);
            setKundenModal(null);
          }}
        />
      )}
    </div>
  );
}
